import React, { useState } from 'react';
import cn from 'classnames';
import { BaseModal } from '@repo/components/modals';
import { capitalize } from '@repo/utils/helpers/string';
import Button from '@components/Button';
import { Toggle } from '@components/FormInputs';
import styles from './Interstitial.module.scss';
import { TrackingFn } from '@components/Integrations/utils';
import {
  ExternalAuthProvider,
  getExternalAuthURL,
  openUrlOutsideElectron,
} from '@repo/utils/helpers/electron-external';
import { useIsElectron, useAuthContext } from '@repo/hooks';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@repo/themes';

interface InterstitialModalProps {
  accountType: ExternalAuthProvider;
  open: boolean;
  closeModal: () => void;
  forceDarkMode: boolean;
  trackConnection: TrackingFn;
}

export default function InterstitialModal({
  accountType,
  open,
  closeModal,
  forceDarkMode,
  trackConnection,
}: InterstitialModalProps) {
  const [theme] = useTheme();
  const { getEntitlementValue } = useAuthContext();
  const isElectron = useIsElectron();
  const location = useLocation();
  const userCanAddEmail = getEntitlementValue('integrations.email');
  const userCanAddContacts = false; //accountType === 'google';

  const [enableCal, setEnableCal] = useState(true);
  const [enableContacts, setEnableContacts] = useState(false);
  const [enableEmail, setEnableEmail] = useState(!!userCanAddEmail);
  const accountLabel = capitalize(accountType);

  const redirectToAuth = async () => {
    const scopes = [enableCal && 'calendar', userCanAddEmail && enableEmail && 'email', enableContacts && 'contacts']
      .filter((scope) => scope)
      .join(',');

    const type = accountType == 'google' ? 'Google' : 'Microsoft';
    // FIXME rewrite this
    // @ts-ignore
    trackConnection({ type, scopes: scopes.includes(',') ? 'all' : scopes });
    const { redirect } = await getExternalAuthURL(
      accountType,
      isElectron ? 'desktop' : 'web',
      scopes,
      location.pathname,
      'connect',
    );
    if (isElectron) {
      openUrlOutsideElectron(redirect);
    } else {
      window.location.href = redirect;
    }
    closeModal();
  };

  let emailIcon = '/img/contact/icons/source/email.svg';
  let calIcon = '/img/contact/icons/source/calendar.svg';
  let contactsIcon = '/img/contact/icons/source/contacts.svg';

  if (!forceDarkMode && theme == 'light') {
    emailIcon = '/img/contact/icons/source/email_light.svg';
    calIcon = '/img/contact/icons/source/calendar_light.svg';
    contactsIcon = '/img/contact/icons/source/contacts_light.svg';
  }

  return (
    <BaseModal
      isOpen={open}
      className="modal-small"
      onRequestClose={closeModal}
      forceDarkMode={forceDarkMode}
    >
      <div className="modal-header">Allow access to your {accountLabel} Account</div>
      <div className={cn(styles.modal, 'modal-content-padded')}>
        <p>
          Please connect your preferred accounts.{' '}
          {userCanAddEmail && <>Most members connect both calendar and email to start.</>}
        </p>
        <p>
          Remember, Clay cannot edit or delete your data, and everything is stored{' '}
          <a href="https://clay.earth/security-and-privacy" target="_blank">
            privately &amp; securely
          </a>
          .
        </p>
        <div className={styles.toggles}>
          <Toggle checked={enableCal} onChange={() => setEnableCal(!enableCal)}>
            <span className={cn(styles.scopeLabel, enableCal && styles.scopeEnabled)}>
              <img src={calIcon} /> {accountLabel} Calendar
            </span>
          </Toggle>
          {userCanAddEmail && (
            <Toggle checked={enableEmail} onChange={() => setEnableEmail(!enableEmail)}>
              <span className={cn(styles.scopeLabel, enableEmail && styles.scopeEnabled)}>
                <img src={emailIcon} /> {accountLabel} Email
              </span>
            </Toggle>
          )}
          {userCanAddContacts && (
            <Toggle checked={enableContacts} onChange={() => setEnableContacts(!enableContacts)}>
              <span className={cn(styles.scopeLabel, enableContacts && styles.scopeEnabled)}>
                <img src={contactsIcon} /> {accountLabel} Contacts
              </span>
            </Toggle>
          )}
        </div>
        <div className={styles.btnRow}>
          <Button className="btn btn-muted" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            disabled={!enableCal && !enableEmail}
            onClick={redirectToAuth}
          >
            Connect
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
